import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import withLoader from "../../components/layouts/WithLoader";
import WarehouseService from "../../services/WarehouseService";
import getCurrentUserId from "../../reduxSaga/sagas/requests/currentUserId";

const SECTIONS = [
  {
    key: "infQctMin",
    title: "Stock sous le minimum",
    hint: "Dépôts à réapprovisionner",
    icon: "fas fa-arrow-down",
    color: "#dc3545",
  },
  {
    key: "supQctMax",
    title: "Stock au-dessus du maximum",
    hint: "Dépôts en surstock",
    icon: "fas fa-arrow-up",
    color: "#28a745",
  },
  {
    key: "reste",
    title: "Stock normal",
    hint: "Entre le minimum et le maximum",
    icon: "fas fa-equals",
    color: "#17a2b8",
  },
];

const EtatStocks = ({ startLoading, stopLoading }) => {
  const navigate = useNavigate();
  const [availableWarehouses, setAvailableWarehouses] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("stockAsc");

  useEffect(() => {
    startLoading();
    WarehouseService.getWarehousesByQctInvalid(getCurrentUserId())
      .then((response) => {
        setAvailableWarehouses(response.data.success[0]);
      })
      .catch((error) => {})
      .finally(() => {
        stopLoading();
      });
  }, []);

  const onOpenDetails = (warehouse) => {
    sessionStorage.setItem("idStockswarehouse", warehouse.id);
    navigate("/EtatStocksDetails");
  };

  const matches = (warehouse) =>
    `${warehouse.ref} ${warehouse.name}`
      .toLowerCase()
      .includes(search.trim().toLowerCase());

  const sorters = {
    stockAsc: (a, b) => (a.stock || 0) - (b.stock || 0),
    stockDesc: (a, b) => (b.stock || 0) - (a.stock || 0),
    name: (a, b) => `${a.name}`.localeCompare(`${b.name}`),
  };

  const listOf = (key) =>
    ((availableWarehouses && availableWarehouses[key]) || [])
      .filter(matches)
      .sort(sorters[sortBy]);

  const visibleSections = SECTIONS.filter(
    (section) => statusFilter === "all" || statusFilter === section.key
  );

  const allCount = SECTIONS.reduce(
    (sum, s) => sum + ((availableWarehouses && availableWarehouses[s.key]) || []).length,
    0
  );

  return (
    <div className="content-wrapper" style={{ marginTop: "0" }}>
      <section className="content-header">
        <div className="container-fluid">
          <div className="d-flex flex-wrap justify-content-between align-items-center">
            <div>
              <h4 className="mb-1">
                <i className="fas fa-chart-bar mr-2"></i>État des stocks
              </h4>
              <small className="text-muted">
                Situation du stock de chaque dépôt par rapport à ses seuils
                minimum et maximum
              </small>
            </div>
            <div className="d-flex flex-wrap align-items-center mt-2 mt-sm-0">
              <button
                className="btn btn-outline-dark ml-sm-2"
                onClick={() => navigate("/analyseStock")}
              >
                <i className="fas fa-search mr-1"></i> Analyser stock
              </button>
              <button
                className="btn btn-outline-dark ml-sm-2"
                onClick={() => navigate("/analyseStockVente")}
              >
                <i className="fas fa-cash-register mr-1"></i> Analyser stock vente
              </button>
              <div className="input-group ml-sm-2" style={{ maxWidth: 300 }}>
                <div className="input-group-prepend">
                  <span className="input-group-text bg-white">
                    <i className="fas fa-search"></i>
                  </span>
                </div>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Chercher un dépôt"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <select
                className="custom-select ml-sm-2"
                style={{ maxWidth: 190 }}
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                title="Trier"
              >
                <option value="stockAsc">Stock croissant</option>
                <option value="stockDesc">Stock décroissant</option>
                <option value="name">Nom du dépôt</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="row mt-3">
            {SECTIONS.map((section) => {
              const count = (
                (availableWarehouses && availableWarehouses[section.key]) ||
                []
              ).length;
              const active = statusFilter === section.key;
              return (
                <div className="col-md-4" key={section.key}>
                  <button
                    type="button"
                    className="d-flex align-items-center p-3 mb-3 bg-white w-100 text-left"
                    title={
                      active
                        ? "Afficher tous les dépôts"
                        : `Afficher seulement : ${section.title.toLowerCase()}`
                    }
                    onClick={() => setStatusFilter(active ? "all" : section.key)}
                    style={{
                      border: active
                        ? `2px solid ${section.color}`
                        : "2px solid transparent",
                      borderLeft: `4px solid ${section.color}`,
                      borderRadius: 8,
                      boxShadow: "0 1px 6px rgba(0,0,0,.08)",
                      cursor: "pointer",
                      outline: "none",
                    }}
                  >
                    <span
                      className="d-flex align-items-center justify-content-center text-white mr-3"
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: 8,
                        backgroundColor: section.color,
                      }}
                    >
                      <i className={section.icon}></i>
                    </span>
                    <div>
                      <small
                        className="text-muted text-uppercase d-block"
                        style={{ fontWeight: 600, letterSpacing: "0.03em" }}
                      >
                        {section.title}
                      </small>
                      <span style={{ fontSize: "1.4rem", fontWeight: 700 }}>
                        {count}
                      </span>
                      <small className="text-muted ml-1">dépôts</small>
                      <small className="d-block text-muted">{section.hint}</small>
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
          <div className="d-flex flex-wrap align-items-center mb-3">
            <small className="text-muted mr-2">
              <i className="fas fa-info-circle mr-1"></i>
              Cliquez sur une carte pour filtrer.
            </small>
            {statusFilter !== "all" && (
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setStatusFilter("all")}
              >
                <i className="fas fa-times mr-1"></i>
                Afficher tous les dépôts
              </button>
            )}
          </div>

          {visibleSections.map((section) => {
            const list = listOf(section.key);
            if (list.length === 0) return null;
            return (
              <div key={section.key} className="mb-4">
                <h6 className="mb-3" style={{ color: section.color }}>
                  <i className={`${section.icon} mr-2`}></i>
                  {section.title}
                  <span
                    className="badge ml-2 text-white"
                    style={{ backgroundColor: section.color }}
                  >
                    {list.length}
                  </span>
                  <small className="text-muted ml-2">{section.hint}</small>
                </h6>
                <div className="row">
                  {list.map((warehouse) => (
                    <div className="col-xl-3 col-lg-4 col-md-6" key={warehouse.id}>
                      <div
                        className="card h-100"
                        role="button"
                        tabIndex={0}
                        style={{
                          borderRadius: 8,
                          boxShadow: "0 1px 6px rgba(0,0,0,.08)",
                          borderTop: `3px solid ${section.color}`,
                          cursor: "pointer",
                        }}
                        onClick={() => onOpenDetails(warehouse)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") onOpenDetails(warehouse);
                        }}
                      >
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start">
                            <div style={{ minWidth: 0 }}>
                              <span className="badge badge-light border mb-1">
                                {warehouse.ref}
                              </span>
                              <div
                                style={{ fontWeight: 600 }}
                                className="text-truncate"
                                title={warehouse.name}
                              >
                                {warehouse.name}
                              </div>
                            </div>
                            <i
                              className="fas fa-warehouse"
                              style={{ color: section.color, opacity: 0.6 }}
                            ></i>
                          </div>
                          <div className="mt-3">
                            <span
                              style={{
                                fontSize: "1.8rem",
                                fontWeight: 700,
                                color: section.color,
                              }}
                            >
                              {warehouse.stock}
                            </span>
                            <small className="text-muted ml-1">en stock</small>
                          </div>
                        </div>
                        <div className="card-footer bg-white text-right py-2">
                          <small style={{ color: section.color, fontWeight: 600 }}>
                            Voir le détail
                            <i className="fas fa-arrow-right ml-1"></i>
                          </small>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {availableWarehouses && allCount === 0 && (
            <div className="text-center text-muted py-5">
              <i className="fas fa-warehouse fa-2x mb-2 d-block"></i>
              Aucun dépôt à afficher
            </div>
          )}
          {availableWarehouses &&
            allCount > 0 &&
            visibleSections.every((s) => listOf(s.key).length === 0) && (
              <div className="text-center text-muted py-5">
                <i className="fas fa-search fa-2x mb-2 d-block"></i>
                Aucun dépôt ne correspond à la recherche
              </div>
            )}
        </div>
      </section>
    </div>
  );
};

export default withLoader(EtatStocks);
